// src/pages/contracts/DirectContractAccess.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, CircularProgress, Container, Chip, IconButton, Divider, 
  Tooltip, Paper, Stack, Fade, Zoom, LinearProgress, Grid,
} from '@mui/material';
import {
  CheckCircle as SignedIcon,
  Description as DocumentIcon,
  Error as ErrorIcon,
  Gavel as ContractIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Article as ArticleIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Verified as VerifiedIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import PageTitle from '../../Components/PageTitle';
import client from '../../API';
import useAuth from '../../Hooks/use-auth';
import { useCreatorAuth } from '../../Hooks/creator-use-auth';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Enhanced light theme with modern styling
const modernTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb',
      light: '#3b82f6',
      dark: '#1e40af',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
  },
});

const statusColor = (status) => {
  switch (status) {
    case 'signed':
    case 'agreed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'declined':
      return 'error';
    default:
      return 'default';
  }
};

const DirectContractAccess = () => {
  const { id: assignmentId } = useParams();
  const navigate = useNavigate();

  const { isAuthorized: isUserAuthorized } = useAuth();
  const { isAuthorized: isCreatorAuthorized, isInitialized } = useCreatorAuth();

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [contractData, setContractData] = useState(null);
  const [error, setError] = useState(null);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [completed, setCompleted] = useState(false);

  const completedDocRef = useRef(null);

  const load = useCallback(async () => {
    if (!assignmentId) {
      setError('Invalid contract access link');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await client.contracts.getContractFromAssignment(assignmentId);
      setContractData(data);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load contract');
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => { load(); }, [load]);

  const currentSigId = contractData?.signature?.id;

  const handleAgree = async () => {
    if (!contractData?.is_simple) return handleSign();
    try {
      setActionLoading(true);
      setError(null);
      const res = await client.contracts.agreeToContract(currentSigId);
      setCompleted(true);
      setContractData(prev => ({ ...prev, signature: res.signature || res.data?.signature }));
      await load();
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSign = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const res = await client.contracts.signContract(currentSigId);
      setCompleted(true);
      setContractData(prev => ({ ...prev, signature: res.signature || res.data?.signature }));
      await load();
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      setError('Please provide a reason for declining');
      return;
    }
    try {
      setActionLoading(true);
      setError(null);
      const res = await client.contracts.declineContract(currentSigId, declineReason);
      setCompleted(true);
      setContractData(prev => ({ ...prev, signature: res.signature || res.data?.signature }));
      setShowDeclineDialog(false);
      await load();
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = () => {
    if (isCreatorAuthorized) {
      navigate('/creatorconnect/start');
    } else {
      navigate('/dashboard');
    }
  };

  const downloadPdf = async () => {
    if (!completedDocRef.current) return;
    const element = completedDocRef.current;
    const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`contract_${contractData?.signature?.id || 'completed'}.pdf`);
  };

  if (!isInitialized) {
    return (
      <ThemeProvider theme={modernTheme}>
        <PageTitle title="Loading Contract - Blitz" />
        <Box sx={{ 
          minHeight: '100vh', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Paper elevation={0} sx={{ p: 6, borderRadius: 4, textAlign: 'center' }}>
            <CircularProgress size={56} thickness={4} />
            <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
              Initializing...
            </Typography>
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  if (loading) {
    return (
      <ThemeProvider theme={modernTheme}>
        <PageTitle title="Loading Contract - Blitz" />
        <Box sx={{ 
          minHeight: '100vh', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Paper elevation={0} sx={{ p: 6, borderRadius: 4, textAlign: 'center' }}>
            <CircularProgress size={56} thickness={4} />
            <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
              Loading contract...
            </Typography>
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={modernTheme}>
        <PageTitle title="Contract Error - Blitz" />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Fade in>
            <Box>
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 4, 
                  borderRadius: 3,
                  '& .MuiAlert-icon': { fontSize: 32 }
                }}
              >
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Contract Access Error
                </Typography>
                <Typography variant="body2">{error}</Typography>
              </Alert>
              <Stack direction="row" spacing={2}>
                <Button 
                  variant="outlined" 
                  onClick={handleClose} 
                  startIcon={<BackIcon />}
                  size="large"
                >
                  Go Back
                </Button>
                <Button 
                  variant="contained" 
                  onClick={load} 
                  startIcon={<RefreshIcon />}
                  size="large"
                >
                  Try Again
                </Button>
              </Stack>
            </Box>
          </Fade>
        </Container>
      </ThemeProvider>
    );
  }

  if (!contractData) {
    return (
      <ThemeProvider theme={modernTheme}>
        <PageTitle title="Contract Not Found - Blitz" />
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Fade in>
            <Box>
              <Alert severity="warning" sx={{ borderRadius: 3, mb: 3 }}>
                Contract not found or access denied.
              </Alert>
              <Button 
                variant="outlined" 
                onClick={handleClose} 
                startIcon={<BackIcon />}
                size="large"
              >
                Go Back
              </Button>
            </Box>
          </Fade>
        </Container>
      </ThemeProvider>
    );
  }

  const { signature, assignment, template, user, company, processed_contract_body, is_simple, all_signatures } = contractData;
  const showSignatureList = ['company', 'team'].includes(assignment?.assignment_scope);

  const signatureRows = (all_signatures || []).sort((a, b) => {
    const order = { signed: 0, agreed: 0, pending: 1, declined: 2 };
    return (order[a.status] ?? 99) - (order[b.status] ?? 99);
  });

  // COMPLETED VIEW
  if (completed || (signature && signature.status !== 'pending')) {
    const isAgreed = signature.status === 'agreed';
    const isSigned = signature.status === 'signed';
    const isDeclined = signature.status === 'declined';
    const completedDate = isSigned
      ? signature.signed_at
      : isAgreed
        ? signature.agreed_at
        : signature.declined_at;

    return (
      <ThemeProvider theme={modernTheme}>
        <PageTitle title={`Contract ${signature.status} - Blitz`} />
        <Box sx={{ 
          minHeight: '100vh', 
          bgcolor: 'background.default',
          py: 4
        }}>
          <Container maxWidth="lg">
            <Fade in>
              <Box>
                {/* Header */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    mb: 4, 
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <IconButton onClick={handleClose} sx={{ bgcolor: 'action.hover' }}>
                    <BackIcon />
                  </IconButton>
                  <Typography variant="h6" fontWeight={600}>
                    Back to {isCreatorAuthorized ? 'Creator Connect' : 'Dashboard'}
                  </Typography>
                </Paper>

                {/* Status Card */}
                <Zoom in>
                  <Card 
                    elevation={0}
                    sx={{ 
                      mb: 4,
                      background: (isAgreed || isSigned) 
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white'
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                      {(isAgreed || isSigned) && (
                        <>
                          <SignedIcon sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
                          <Typography variant="h3" gutterBottom fontWeight={700}>
                            {isAgreed ? 'Agreement Confirmed!' : 'Contract Signed!'}
                          </Typography>
                          <Typography variant="h6" sx={{ opacity: 0.9 }}>
                            Thank you for {isAgreed ? 'agreeing to' : 'signing'} the contract.
                          </Typography>
                        </>
                      )}
                      {isDeclined && (
                        <>
                          <ErrorIcon sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
                          <Typography variant="h3" gutterBottom fontWeight={700}>
                            Contract Declined
                          </Typography>
                          <Typography variant="h6" sx={{ opacity: 0.9 }}>
                            You have declined this contract.
                          </Typography>
                          {signature.decline_reason && (
                            <Paper 
                              elevation={0}
                              sx={{ 
                                mt: 3, 
                                p: 2, 
                                bgcolor: 'rgba(255,255,255,0.2)',
                                borderRadius: 2,
                                maxWidth: 600,
                                mx: 'auto'
                              }}
                            >
                              <Typography variant="body2" fontWeight={500}>
                                Reason: {signature.decline_reason}
                              </Typography>
                            </Paper>
                          )}
                        </>
                      )}

                      <Paper 
                        elevation={0}
                        sx={{ 
                          mt: 4, 
                          p: 3, 
                          bgcolor: 'rgba(255,255,255,0.95)',
                          borderRadius: 3,
                          maxWidth: 600,
                          mx: 'auto'
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ArticleIcon color="primary" />
                            <Typography variant="h6" color="text.primary" fontWeight={600}>
                              {template?.name || 'Contract'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon color="action" fontSize="small" />
                            <Typography variant="body1" color="text.secondary">
                              {company?.name || 'Company'}
                            </Typography>
                          </Box>
                          {completedDate && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ScheduleIcon color="action" fontSize="small" />
                              <Typography variant="body2" color="text.secondary">
                                Completed on {new Date(completedDate).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Paper>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }} justifyContent="center">
                        <Button 
                          variant="contained" 
                          onClick={handleClose} 
                          startIcon={<BackIcon />}
                          size="large"
                          sx={{ 
                            bgcolor: 'white',
                            color: isDeclined ? 'error.main' : 'success.main',
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.9)'
                            }
                          }}
                        >
                          Back to {isCreatorAuthorized ? 'Creator Connect' : 'Dashboard'}
                        </Button>
                        {(isSigned || isAgreed || isDeclined) && (
                          <Button 
                            variant="outlined" 
                            startIcon={<DownloadIcon />} 
                            onClick={downloadPdf}
                            size="large"
                            sx={{ 
                              borderColor: 'white',
                              color: 'white',
                              '&:hover': {
                                borderColor: 'white',
                                bgcolor: 'rgba(255,255,255,0.1)'
                              }
                            }}
                          >
                            Download PDF
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Zoom>

                {/* Signature List */}
                {showSignatureList && signatureRows.length > 0 && (
                  <Card elevation={0} sx={{ mb: 4, borderRadius: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 3 }}>
                        Organization Signature Status
                      </Typography>
                      <Stack spacing={2}>
                        {signatureRows.map(s => (
                          <Paper 
                            key={s.id}
                            elevation={0}
                            sx={{ 
                              p: 2.5, 
                              bgcolor: s.id === signature.id ? 'primary.50' : 'background.default',
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: s.id === signature.id ? 'primary.main' : 'divider',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box
                                sx={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: '50%',
                                  bgcolor: `${statusColor(s.status)}.main`,
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 700,
                                  fontSize: '1.5rem'
                                }}
                              >
                                {s.user?.first_name?.[0] || 'U'}
                              </Box>
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Typography variant="body1" fontWeight={700}>
                                    {s.user
                                      ? `${s.user.first_name} ${s.user.last_name}`
                                      : `User ${s.user_id}`}
                                  </Typography>
                                  {s.id === signature.id && (
                                    <Chip size="small" label="You" color="primary" sx={{ fontWeight: 600 }} />
                                  )}
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <EmailIcon sx={{ fontSize: 14 }} />
                                  {s.user?.email || '—'}
                                </Typography>
                                {s.status !== 'pending' && (
                                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    {s.status === 'signed' && s.signed_at && `Signed: ${new Date(s.signed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                                    {s.status === 'agreed' && s.agreed_at && `Agreed: ${new Date(s.agreed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                                    {s.status === 'declined' && s.declined_at && `Declined: ${new Date(s.declined_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Chip 
                              label={s.status.charAt(0).toUpperCase() + s.status.slice(1)} 
                              color={statusColor(s.status)} 
                              sx={{ fontWeight: 600, fontSize: '13px' }}
                            />
                          </Paper>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* Professional Signed Contract Document */}
                <Box 
                  sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    p: { xs: 2, md: 4 },
                    bgcolor: '#e5e7eb'
                  }}
                >
                  <Paper 
                    ref={completedDocRef}
                    elevation={3}
                    sx={{ 
                      width: '8.5in',
                      maxWidth: '100%',
                      minHeight: '11in',
                      bgcolor: 'white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      position: 'relative',
                      overflow: 'hidden',
                      '@media print': {
                        boxShadow: 'none',
                        margin: 0,
                        width: '100%',
                      }
                    }}
                  >
                    {/* Watermark */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%) rotate(-45deg)',
                        opacity: 0.03,
                        fontSize: { xs: '80px', md: '140px' },
                        fontWeight: 900,
                        color: isDeclined ? 'error.main' : 'success.main',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        zIndex: 0,
                        letterSpacing: '0.1em'
                      }}
                    >
                      {isDeclined ? 'DECLINED' : isAgreed ? 'AGREED' : 'SIGNED'}
                    </Box>

                    {/* Document Header */}
                    <Box sx={{ 
                      p: { xs: 2, md: '1in' },
                      pb: { xs: 2, md: 4 },
                      borderBottom: '4px solid',
                      borderColor: isDeclined ? 'error.main' : 'success.main',
                      background: isDeclined 
                        ? 'linear-gradient(180deg, #FEF2F2 0%, #ffffff 100%)'
                        : 'linear-gradient(180deg, #F0FDF4 0%, #ffffff 100%)',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box sx={{ 
                          width: { xs: 48, md: 64 }, 
                          height: { xs: 48, md: 64 },
                          borderRadius: 2,
                          bgcolor: isDeclined ? 'error.main' : 'success.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          {isDeclined ? (
                            <ErrorIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'white' }} />
                          ) : (
                            <VerifiedIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'white' }} />
                          )}
                        </Box>
                        
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="h3" 
                            sx={{ 
                              fontWeight: 800,
                              color: '#0f172a',
                              fontSize: { xs: '24px', md: '36px' },
                              mb: 1,
                              lineHeight: 1.2
                            }}
                          >
                            {template?.name || 'Contract'}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip 
                              label={isDeclined ? 'DECLINED' : isAgreed ? 'AGREEMENT CONFIRMED' : 'DIGITALLY SIGNED'} 
                              size="small"
                              sx={{ 
                                bgcolor: isDeclined ? 'error.main' : 'success.main',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '11px',
                                letterSpacing: '0.5px'
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {company?.name || 'Company'}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Contract Content */}
                    <Box
                      sx={{ 
                        p: { xs: 2, md: '1in' },
                        pt: { xs: 4, md: 6 },
                        fontFamily: 'Georgia, "Times New Roman", serif',
                        lineHeight: 1.8,
                        fontSize: { xs: '14px', md: '16px' },
                        color: '#1e293b',
                        position: 'relative',
                        zIndex: 1,
                        '& h1': { 
                          fontFamily: '"Inter", sans-serif',
                          fontSize: { xs: '20px', md: '24px' },
                          fontWeight: 700,
                          color: '#0f172a',
                          marginTop: { xs: '24px', md: '32px' },
                          marginBottom: { xs: '12px', md: '16px' }
                        },
                        '& h2': { 
                          fontFamily: '"Inter", sans-serif',
                          fontSize: { xs: '18px', md: '20px' },
                          fontWeight: 600,
                          color: '#0f172a',
                          marginTop: { xs: '20px', md: '28px' },
                          marginBottom: { xs: '10px', md: '14px' }
                        },
                        '& h3': { 
                          fontFamily: '"Inter", sans-serif',
                          fontSize: { xs: '16px', md: '18px' },
                          fontWeight: 600,
                          color: '#1e293b',
                          marginTop: { xs: '16px', md: '24px' },
                          marginBottom: { xs: '8px', md: '12px' }
                        },
                        '& p': { 
                          marginBottom: { xs: '14px', md: '18px' },
                          textAlign: 'justify',
                          lineHeight: 1.8
                        },
                        '& strong': { 
                          fontWeight: 600,
                          color: '#0f172a'
                        },
                        '& ol, & ul': {
                          marginTop: { xs: '12px', md: '16px' },
                          marginBottom: { xs: '12px', md: '16px' },
                          paddingLeft: { xs: '20px', md: '28px' }
                        },
                        '& li': {
                          marginBottom: { xs: '8px', md: '12px' }
                        }
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: processed_contract_body || template?.contract_body || '' 
                      }}
                    />

                    {/* Signature Information Section */}
                    <Box sx={{ 
                      p: { xs: 2, md: '1in' },
                      pt: { xs: 4, md: 6 },
                      borderTop: '2px solid #e5e7eb',
                      bgcolor: '#fafbfc',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      <Typography 
                        variant="overline" 
                        sx={{ 
                          fontWeight: 700,
                          color: 'text.secondary',
                          letterSpacing: '1px',
                          display: 'block',
                          mb: 3
                        }}
                      >
                        SIGNATURE DETAILS
                      </Typography>

                      <Stack spacing={3}>
                        {/* Signer Information Card */}
                        <Paper 
                          elevation={0}
                          sx={{ 
                            p: 3,
                            border: '2px solid',
                            borderColor: isDeclined ? 'error.main' : 'success.main',
                            borderRadius: 2,
                            bgcolor: 'white'
                          }}
                        >
                          <Stack spacing={2}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1.5,
                              pb: 2,
                              borderBottom: '2px solid',
                              borderColor: isDeclined ? 'error.light' : 'success.light'
                            }}>
                              {isDeclined ? (
                                <ErrorIcon sx={{ color: 'error.main', fontSize: 32 }} />
                              ) : (
                                <VerifiedIcon sx={{ color: 'success.main', fontSize: 32 }} />
                              )}
                              <Box>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 800, 
                                  color: isDeclined ? 'error.main' : 'success.main',
                                  letterSpacing: '0.5px',
                                  fontSize: { xs: '16px', md: '18px' }
                                }}>
                                  {isDeclined ? 'CONTRACT DECLINED' : isAgreed ? 'AGREEMENT CONFIRMED' : 'DIGITALLY SIGNED'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {isDeclined ? 'This contract was declined' : 'Cryptographically verified and timestamped'}
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                  SIGNATORY NAME
                                </Typography>
                                <Typography variant="body1" fontWeight={700} color="text.primary">
                                  {user ? `${user.first_name} ${user.last_name}` : 'User'}
                                </Typography>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                  EMAIL ADDRESS
                                </Typography>
                                <Typography variant="body2" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <EmailIcon sx={{ fontSize: 16 }} />
                                  {user?.email || '—'}
                                </Typography>
                              </Grid>

                              {completedDate && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                    {isDeclined ? 'DECLINED ON' : isAgreed ? 'AGREED ON' : 'SIGNED ON'}
                                  </Typography>
                                  <Typography variant="body2" color="text.primary" fontWeight={600}>
                                    {new Date(completedDate).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      timeZoneName: 'short'
                                    })}
                                  </Typography>
                                </Grid>
                              )}

                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                  ORGANIZATION
                                </Typography>
                                <Typography variant="body2" color="text.primary" fontWeight={600}>
                                  {company?.name || 'Company'}
                                </Typography>
                              </Grid>
                            </Grid>

                            {isDeclined && signature.decline_reason && (
                              <Box sx={{ 
                                mt: 2,
                                p: 2,
                                bgcolor: '#FEF2F2',
                                borderRadius: 1,
                                border: '1px solid #FCA5A5'
                              }}>
                                <Typography variant="caption" color="error.dark" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                  REASON FOR DECLINING
                                </Typography>
                                <Typography variant="body2" color="error.dark">
                                  {signature.decline_reason}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Paper>

                        {/* Technical Verification Details */}
                        <Paper 
                          elevation={0}
                          sx={{ 
                            p: 2,
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 1
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5 }}>
                            VERIFICATION DATA
                          </Typography>
                          <Grid container spacing={1.5}>
                            {signature.ip_address && (
                              <Grid item xs={12} sm={6}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Box sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    bgcolor: 'primary.main' 
                                  }} />
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '10px' }}>
                                      IP Address
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                      {signature.ip_address}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Grid>
                            )}
                            
                            <Grid item xs={12} sm={6}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Box sx={{ 
                                  width: 6, 
                                  height: 6, 
                                  borderRadius: '50%', 
                                  bgcolor: 'primary.main' 
                                }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '10px' }}>
                                    Signature ID
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                    {signature.id}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Grid>

                            {signature.signature_hash && (
                              <Grid item xs={12}>
                                <Stack direction="row" alignItems="flex-start" spacing={1}>
                                  <Box sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    bgcolor: 'primary.main',
                                    mt: 0.5
                                  }} />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '10px' }}>
                                      Cryptographic Hash
                                    </Typography>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontFamily: 'monospace',
                                        fontSize: '11px',
                                        wordBreak: 'break-all',
                                        color: 'text.secondary'
                                      }}
                                    >
                                      {signature.signature_hash}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Grid>
                            )}
                          </Grid>
                        </Paper>

                        {/* Document Footer */}
                        <Box sx={{ 
                          pt: 3,
                          borderTop: '1px solid #e5e7eb',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 2
                        }}>
                          <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {company?.name || 'Blitz Systems Corp'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Digital Contract Management Platform
                            </Typography>
                          </Stack>
                          
                          <Stack spacing={0.5} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Contract ID: {signature.id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Generated: {new Date().toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Verification Badge - Bottom Right Corner */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: { xs: 16, md: 32 },
                        right: { xs: 16, md: 32 },
                        zIndex: 2
                      }}
                    >
                      <Paper
                        elevation={4}
                        sx={{
                          p: 1.5,
                          bgcolor: isDeclined ? 'error.main' : 'success.main',
                          color: 'white',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        {isDeclined ? (
                          <ErrorIcon sx={{ fontSize: 24 }} />
                        ) : (
                          <VerifiedIcon sx={{ fontSize: 24 }} />
                        )}
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2 }}>
                            VERIFIED
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '9px', opacity: 0.9 }}>
                            {new Date(completedDate).getFullYear()}
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            </Fade>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  // PENDING VIEW
  return (
    <ThemeProvider theme={modernTheme}>
      <PageTitle title={`${template?.name || 'Contract'} - Blitz`} />
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: 'background.default', 
        py: 4,
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)'
      }}>
        <Container maxWidth="lg">
          <Fade in>
            <Box>
              {/* Header */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 4, 
                  borderRadius: 3,
                  background: 'white',
                  border: '1px solid #e2e8f0'
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={2}>
                    <IconButton onClick={handleClose} sx={{ bgcolor: 'action.hover' }}>
                      <BackIcon />
                    </IconButton>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="text.primary">
                        {template?.name || 'Contract'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {company?.name || 'Company'} • Contract Review & Signature
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip
                    label={is_simple ? 'Simple Agreement' : 'Formal Contract'}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
              </Paper>

              {/* Info Alerts */}
              {user && (
                <Alert 
                  severity="info" 
                  sx={{ mb: 3, borderRadius: 3 }}
                  icon={<PersonIcon />}
                >
                  <Typography variant="body2" fontWeight={500}>
                    Assigned to: {user.first_name} {user.last_name} ({user.email})
                  </Typography>
                </Alert>
              )}

              {assignment?.assignment_message && (
                <Alert 
                  severity="warning" 
                  sx={{ mb: 3, borderRadius: 3 }}
                  icon={<ArticleIcon />}
                >
                  <Typography variant="body2" fontWeight={500}>
                    Message: {assignment.assignment_message}
                  </Typography>
                </Alert>
              )}

              {assignment?.due_date && (
                <Alert 
                  severity="warning" 
                  sx={{ mb: 3, borderRadius: 3 }}
                  icon={<ScheduleIcon />}
                >
                  <Typography variant="body2" fontWeight={500}>
                    Due Date: {new Date(assignment.due_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Alert>
              )}

              {/* Signature List */}
              {showSignatureList && signatureRows.length > 0 && (
                <Card elevation={0} sx={{ mb: 4, borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 1 }}>
                      Organization Signatures
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                      Track who has completed this {is_simple ? 'agreement' : 'contract'}
                    </Typography>
                    <Stack spacing={2}>
                      {signatureRows.map(s => (
                        <Paper 
                          key={s.id}
                          elevation={0}
                          sx={{ 
                            p: 2.5, 
                            bgcolor: s.id === signature.id ? 'primary.50' : 'background.default',
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: s.id === signature.id ? 'primary.main' : 'divider',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                bgcolor: `${statusColor(s.status)}.main`,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                fontSize: '1.5rem'
                              }}
                            >
                              {s.user?.first_name?.[0] || 'U'}
                            </Box>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="body1" fontWeight={700}>
                                  {s.user
                                    ? `${s.user.first_name} ${s.user.last_name}`
                                    : `User ${s.user_id}`}
                                </Typography>
                                {s.id === signature.id && (
                                  <Chip size="small" label="You" color="primary" sx={{ fontWeight: 600 }} />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 14 }} />
                                {s.user?.email || '—'}
                              </Typography>
                              {s.status !== 'pending' && (
                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                  {s.status === 'signed' && s.signed_at && `Signed: ${new Date(s.signed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                                  {s.status === 'agreed' && s.agreed_at && `Agreed: ${new Date(s.agreed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                                  {s.status === 'declined' && s.declined_at && `Declined: ${new Date(s.declined_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Chip 
                            label={s.status.charAt(0).toUpperCase() + s.status.slice(1)} 
                            color={statusColor(s.status)}
                            sx={{ fontWeight: 600, fontSize: '13px' }}
                          />
                        </Paper>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Professional Contract Document */}
              <Card 
                elevation={0} 
                sx={{ 
                  mb: 4, 
                  borderRadius: 3,
                  border: '2px solid #e2e8f0',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  bgcolor: '#f8fafc',
                  borderBottom: '2px solid #e2e8f0',
                  p: 3
                }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <ArticleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        Contract Document
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please review carefully before signing
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      maxHeight: '600px',
                      overflow: 'auto',
                      p: { xs: 3, md: 5 },
                      bgcolor: 'white',
                      '&::-webkit-scrollbar': {
                        width: '12px',
                      },
                      '&::-webkit-scrollbar-track': {
                        bgcolor: '#f1f5f9',
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        bgcolor: '#cbd5e1',
                        borderRadius: '10px',
                        '&:hover': {
                          bgcolor: '#94a3b8',
                        }
                      },
                    }}
                  >
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: processed_contract_body || template?.contract_body || 'Loading contract content...'
                      }}
                      sx={{ 
                        fontFamily: 'Georgia, "Times New Roman", serif', 
                        lineHeight: 1.8,
                        fontSize: '1.05rem',
                        color: '#1e293b',
                        '& h1, & h2, & h3': {
                          fontFamily: '"Inter", sans-serif',
                          fontWeight: 700,
                          color: '#0f172a',
                          marginTop: '2rem',
                          marginBottom: '1rem'
                        },
                        '& p': {
                          marginBottom: '1rem',
                          textAlign: 'justify'
                        },
                        '& strong': {
                          fontWeight: 600,
                          color: '#0f172a'
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* Legal Notice */}
              <Alert 
                severity="info" 
                sx={{ mb: 4, borderRadius: 3 }}
                icon={<VerifiedIcon />}
              >
                <Typography variant="body2" fontWeight={500}>
                  {is_simple
                    ? 'By clicking "I Agree", you confirm that you have read and agree to be bound by the terms above. Your agreement will be legally binding.'
                    : 'By clicking "Sign Contract", you agree to be legally bound by the terms and conditions outlined above. Your digital signature will be recorded with timestamp, IP address, and cryptographic hash for legal verification and authenticity.'}
                </Typography>
              </Alert>

              {/* Professional Action Buttons */}
              <Card elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <CardContent sx={{ p: 4, bgcolor: '#f8fafc' }}>
                  <Stack spacing={3}>
                    <Typography variant="h6" fontWeight={600} textAlign="center" color="text.primary">
                      Ready to {is_simple ? 'Agree' : 'Sign'}?
                    </Typography>
                    
                    <Stack 
                      direction={{ xs: 'column-reverse', sm: 'row' }} 
                      spacing={3} 
                      justifyContent="center"
                      alignItems="stretch"
                    >
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setShowDeclineDialog(true)}
                        disabled={actionLoading}
                        size="large"
                        sx={{ 
                          minWidth: { xs: '100%', sm: 180 },
                          py: 1.5,
                          borderWidth: 2,
                          '&:hover': {
                            borderWidth: 2
                          }
                        }}
                      >
                        Decline Contract
                      </Button>

                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleAgree}
                        disabled={actionLoading}
                        startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <VerifiedIcon />}
                        sx={{ 
                          minWidth: { xs: '100%', sm: 280 },
                          py: 2,
                          fontSize: '1.1rem',
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                          boxShadow: '0 8px 24px rgba(37, 99, 235, 0.4)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 32px rgba(37, 99, 235, 0.5)',
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {actionLoading 
                          ? (is_simple ? 'Processing Agreement...' : 'Processing Signature...') 
                          : (is_simple ? 'I Agree to Terms' : 'Sign Contract Now')}
                      </Button>
                    </Stack>

                    {actionLoading && (
                      <Box>
                        <LinearProgress sx={{ borderRadius: 1 }} />
                        <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mt: 1, display: 'block' }}>
                          Securely processing your {is_simple ? 'agreement' : 'signature'}...
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {error && (
                <Fade in>
                  <Alert severity="error" sx={{ mt: 3, borderRadius: 3 }}>
                    {error}
                  </Alert>
                </Fade>
              )}

              {/* Decline Dialog */}
              <Dialog 
                open={showDeclineDialog} 
                onClose={() => setShowDeclineDialog(false)} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                  sx: { borderRadius: 3 }
                }}
              >
                <DialogTitle sx={{ pb: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                      Decline Contract
                    </Typography>
                    <IconButton onClick={() => setShowDeclineDialog(false)} size="small">
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 3 }}>
                  <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                    Please provide a reason for declining this {is_simple ? 'agreement' : 'contract'}:
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter your reason for declining..."
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2 }}>
                  <Button 
                    onClick={() => setShowDeclineDialog(false)} 
                    disabled={actionLoading}
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDecline}
                    disabled={!declineReason.trim() || actionLoading}
                    startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {actionLoading ? 'Declining...' : 'Confirm Decline'}
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </Fade>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default DirectContractAccess;